

import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { decryptResponse } from "./../CryptoUtil";
import { api } from "./../../App";

const ACTIONS = [
  { title: "Add ZSM", subtitle: "Zonal Sales Manager", path: "add-zsm" },
  { title: "Add ASM", subtitle: "Area Sales Manager", path: "add-asm" },
  { title: "Add SM", subtitle: "Sales Manager", path: "add-sm" },
  { title: "Add Dealer", subtitle: "Dealer Management", path: "add-dealer" },
];

// Change these two paths if your router uses different transfer route names.
const TRANSFERS = [
  { title: "ASM Transfer", subtitle: "Transfer an ASM", path: "asm-transfer" },
  { title: "SM Transfer", subtitle: "Transfer an SM", path: "sm-transfer" },
];

const STATS = [
  { key: "zsm", label: "Total ZSM", letter: "Z", color: "primary" },
  { key: "asm", label: "Total ASM", letter: "A", color: "success" },
  { key: "sm", label: "Total SM", letter: "S", color: "warning" },
  { key: "dealers", label: "Total Dealers", letter: "D", color: "info" },
];

const getSession = () => {
  try {
    return JSON.parse(localStorage.getItem("userSession") || "null");
  } catch {
    return null;
  }
};

const decodeResponse = (raw) => {
  if (raw?.encrypted?.payload !== undefined) {
    return decryptResponse(raw.encrypted.payload, raw.encrypted.transactionId);
  }

  if (raw?.payload !== undefined && raw?.transactionId) {
    return decryptResponse(raw.payload, raw.transactionId);
  }

  return raw;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const session = getSession();
  const user = session?.user || {};

  const [admin, setAdmin] = useState({
    name: String(user.name ?? user.full_name ?? "Admin"),
    id: String(user.code ?? user.user_code ?? ""),
  });

  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const currentSession = getSession();
      const token = currentSession?.token;

      if (!token) {
        throw new Error("Login session missing. Please login again.");
      }

      const response = await axios.get(`${api}adminDashboard.php`, {
        withCredentials: true,
        timeout: 15000,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = decodeResponse(response.data);

      if (result?.success !== true) {
        throw new Error(result?.message || "Unable to load dashboard.");
      }

      setCounts({
        zsm: Number(result?.counts?.zsm ?? 0),
        asm: Number(result?.counts?.asm ?? 0),
        sm: Number(result?.counts?.sm ?? 0),
        dealers: Number(result?.counts?.dealers ?? 0),
      });

      setAdmin((previous) => ({
        name: String(result?.admin?.name || previous.name),
        id: String(result?.admin?.id || previous.id),
      }));
    } catch (err) {
      let message = err.message || "Unable to load dashboard totals.";

      try {
        if (err.response?.data) {
          const result = decodeResponse(err.response.data);
          message = result?.message || message;
        }
      } catch {
        // Keep the original error message.
      }

      setCounts(null);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const ActionCard = ({ title, subtitle, path, transfer = false }) => (
    <div className="col-12 col-md-6 col-xl-3 mb-3">
      <button
        type="button"
        className="card border rounded-4 h-100 w-100 text-start bg-white"
        onClick={() => navigate(path)}
      >
        <div className="card-body d-flex align-items-center p-3">
          <div className="bg-primary bg-opacity-10 rounded-4 d-flex align-items-center justify-content-center me-3 flex-shrink-0 p-3">
            <span className="text-primary fs-4">{transfer ? "⇄" : "+"}</span>
          </div>
          <div>
            <h6 className="fw-bold mb-1">{title}</h6>
            <p className="text-secondary small mb-0">{subtitle}</p>
          </div>
        </div>
      </button>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      <div className="row align-items-start mb-5">
        <div className="col">
          <h1 className="fw-bold mb-1">Dashboard</h1>
          <p className="text-secondary fs-5 mb-0">Welcome back, {admin.name}</p>
        </div>
        <div className="col-auto">
          <span className="badge bg-white text-primary border rounded-3 px-3 py-3 fs-6">
            {admin.id || "Admin"}
          </span>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button type="button" className="btn btn-sm btn-outline-danger ms-3" onClick={loadDashboard}>
            Retry
          </button>
        </div>
      )}

         <div className="row mb-4">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <div>
            <h4 className="fw-bold mb-1">Overview</h4>
            <p className="text-secondary small mb-0">
              Active records excluding default ZSM, ASM and SM
            </p>
          </div>
          <button type="button" className="btn btn-outline-primary btn-sm" disabled={loading} onClick={loadDashboard}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        {STATS.map((stat) => (
          <div className="col-12 col-sm-6 col-xl-3 mb-3" key={stat.key}>
            <div className="card border rounded-4 h-100">
              <div className="card-body d-flex align-items-center p-3">
                <div className={`bg-${stat.color} bg-opacity-10 rounded-3 d-flex align-items-center justify-content-center me-3 p-2`}>
                  <span className={`text-${stat.color} fw-bold fs-5`}>{stat.letter}</span>
                </div>
                <div>
                  <p className="text-secondary small mb-0">{stat.label}</p>
                  <h4 className="fw-bold mb-0">
                    {loading ? "..." : counts ? counts[stat.key].toLocaleString("en-IN") : "—"}
                  </h4>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row mb-5">
        <div className="col-12 mb-3">
          <h4 className="fw-bold mb-1">Quick Actions</h4>
          <p className="text-secondary mb-0">Manage your sales hierarchy</p>
        </div>
        {ACTIONS.map((action) => <ActionCard key={action.path} {...action} />)}
      </div>

      <div className="row mb-5">
        <div className="col-12 mb-3">
          <h4 className="fw-bold mb-1">Transfers</h4>
          <p className="text-secondary mb-0">Manage ASM and SM transfers</p>
        </div>
        {TRANSFERS.map((action) => (
          <ActionCard key={action.path} {...action} transfer />
        ))}
      </div>

   

      <div className="row">
        <div className="col-12">
          <div className="card border rounded-4">
            <div className="card-body p-4">
              <div className="row align-items-center">
                <div className="col-12 col-md-4 mb-3 mb-md-0">
                  <p className="text-secondary mb-1">Logged-in Admin</p>
                  <h5 className="fw-bold mb-0">{admin.name}</h5>
                </div>
                <div className="col-12 col-md-4 mb-3 mb-md-0">
                  <p className="text-secondary mb-1">Admin ID</p>
                  <h5 className="fw-bold mb-0">{admin.id || "—"}</h5>
                </div>
                <div className="col-12 col-md-4">
                  <p className="text-secondary mb-1">Account</p>
                  <h5 className="fw-bold text-success mb-0">Active</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
